import { Injectable, OnDestroy } from "@angular/core";
import { Subject, BehaviorSubject, Observable } from "rxjs";
import { share } from "rxjs/operators";
import { AppGlobals } from "../app.globals";
import { IUser } from "../_models";

@Injectable({ providedIn: "root" })
export class StorageService implements OnDestroy {
    private onSubject = new Subject<{ key: string, value: any }>();
    public changes = this.onSubject.asObservable().pipe(share());

    private currentUserSubject: BehaviorSubject<IUser>;
    public currentUser: Observable<IUser>;

    constructor(private appGlobals: AppGlobals) {
        const currentUser = this.getStorage(this.appGlobals.localStorageKeys.currentUser);

        this.currentUserSubject = new BehaviorSubject<IUser>(currentUser);
        this.currentUser = this.currentUserSubject.asObservable();

        this.start();
    }

    ngOnDestroy() {
        this.stop();
    }

    public getStorage(key) {
        if (key !== this.appGlobals.localStorageKeys.currentUser) {
            return JSON.parse(localStorage.getItem(key));
        }
        else {
            return JSON.parse(sessionStorage.getItem(key));
        }
    }

    public store(key: string, data: any): void {
        if (key !== this.appGlobals.localStorageKeys.currentUser) {
            localStorage.setItem(key, JSON.stringify(data));
        }
        else {
            sessionStorage.setItem(key, JSON.stringify(data));

            if (key === this.appGlobals.localStorageKeys.currentUser)
                this.currentUserSubject.next(data);

            if (sessionStorage.getItem(key)) {
                //refresh token
                localStorage.setItem(this.appGlobals.localStorageKeys.sessionRefresh, sessionStorage.getItem(this.appGlobals.localStorageKeys.currentUser));
                localStorage.removeItem(this.appGlobals.localStorageKeys.sessionRefresh);
            }
        }

        // the local application doesn't seem to catch changes to localStorage...
        this.onSubject.next({ key: key, value: data });
    }

    public clear(key) {
        if (key !== this.appGlobals.localStorageKeys.currentUser) {
            localStorage.removeItem(key);
        } else {
            sessionStorage.removeItem(key);
        }

        // the local application doesn't seem to catch changes to localStorage...
        this.onSubject.next({ key: key, value: null });
    }


    private start(): void {
        window.addEventListener("storage", this.storageEventListener.bind(this));
    }

    private storageEventListener(event: StorageEvent) {
        let newValue: any;

        try {
            newValue = JSON.parse(event.newValue);
        } catch (e) {
            newValue = event.newValue;
        }

        if (event.key === this.appGlobals.localStorageKeys.timer) {
            // Some tab asked for the sessionStorage -> send it

            localStorage.setItem(this.appGlobals.localStorageKeys.sessionStorage, sessionStorage.getItem(this.appGlobals.localStorageKeys.currentUser));
            localStorage.removeItem(this.appGlobals.localStorageKeys.sessionStorage);
        } else {
            if (event.key === this.appGlobals.localStorageKeys.sessionStorage /*|| event.key === this.appGlobals.localStorageKeys.sessionRefresh)*/ && !sessionStorage.length) {
                // sessionStorage is empty -> fill it

                sessionStorage.setItem(this.appGlobals.localStorageKeys.currentUser, JSON.stringify(newValue));

                this.currentUserSubject.next(newValue);
            } else
                if (event.key === this.appGlobals.localStorageKeys.sessionRefresh && newValue) {
                    // refresh token in all tabs
                    sessionStorage.setItem(this.appGlobals.localStorageKeys.currentUser, JSON.stringify(newValue));

                    this.currentUserSubject.next(newValue);
                }
        }

        this.onSubject.next({ key: event.key, value: newValue });
    }

    private stop(): void {
        window.removeEventListener("storage", this.storageEventListener.bind(this));

        this.onSubject.complete();
    }
}
